/**
 * Shopper-facing loading state shown while an auto-walk node sequence
 * runs (integration → expression → decision → wait …). Replaces the
 * previous `<p>Working…</p>` flash.
 *
 * Three Cs:
 *   - Configurable: per-node narration strings are operator-authored on
 *     the node config (CheckoutIntegrationConfig.narration etc.) and the
 *     tenant overrides the minimum dwell with `tenant.checkoutLoading.minStepMs`.
 *   - Composable: this is a presentational component fed from the
 *     CheckoutFlowRunner; it has no domain coupling.
 *   - Conversational: the narration strings are operator-authored copy
 *     that an LLM can paraphrase or translate.
 *
 * See `docs/superpowers/specs/2026-05-17-admin-ux-coherence-design.md` §8.
 */
import type { JSX } from 'preact';
import type { CheckoutBlockDto } from './api.js';
import { type SkeletonPrimitive, blocksToSkeleton } from './blocks-to-skeleton.js';

export interface NarratedAutoWalkStep {
  nodeId: string;
  narration: string;
}

export interface NarratedAutoWalkProgressProps {
  completed: NarratedAutoWalkStep[];
  running: NarratedAutoWalkStep | null;
  pending: NarratedAutoWalkStep[];
  nextPageBlocks: CheckoutBlockDto[];
  locale: string;
  minStepMs: number;
}

/** Render the narrated loading card; null when there's nothing to show. */
export function NarratedAutoWalkProgress(props: NarratedAutoWalkProgressProps): JSX.Element | null {
  const hasSteps = props.completed.length + props.pending.length + (props.running ? 1 : 0) > 0;
  const hasSkeleton = props.nextPageBlocks.length > 0;
  if (!hasSteps && !hasSkeleton) return null;

  const skeleton = blocksToSkeleton(props.nextPageBlocks);

  return (
    <div class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-4">
      {hasSteps && (
        <ol class="space-y-2 text-body-sm">
          {props.completed.map((s) => (
            <li key={s.nodeId} class="flex items-center gap-2 text-on-surface">
              <span class="material-symbols-outlined text-[16px] text-success" aria-hidden="true">
                check_circle
              </span>
              <span>{s.narration}</span>
            </li>
          ))}
          {props.running && (
            <li class="flex items-center gap-2 text-on-surface font-medium">
              <span
                class="material-symbols-outlined text-[16px] text-primary motion-safe:animate-pulse"
                aria-hidden="true"
              >
                hourglass_top
              </span>
              <span>{props.running.narration}</span>
              <span class="ml-2 inline-block h-1 w-24 bg-outline-variant rounded overflow-hidden">
                <span class="block h-full w-1/2 bg-primary motion-safe:animate-pulse" />
              </span>
            </li>
          )}
          {props.pending.map((s) => (
            <li key={s.nodeId} class="flex items-center gap-2 text-on-surface-variant">
              <span class="material-symbols-outlined text-[16px]" aria-hidden="true">
                radio_button_unchecked
              </span>
              <span>{s.narration}</span>
            </li>
          ))}
        </ol>
      )}
      {hasSkeleton && (
        <div class="space-y-3 border-t border-outline-variant pt-4">
          <div class="text-[10px] uppercase tracking-widest text-on-surface-variant">Up next</div>
          {skeleton.map((p, i) => (
            <Skeleton key={`${p.kind}-${i}`} primitive={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function Skeleton(props: { primitive: SkeletonPrimitive }): JSX.Element {
  const { primitive: p } = props;
  switch (p.kind) {
    case 'heading':
      return <div data-skeleton class="h-6 w-1/2 bg-outline-variant/40 rounded motion-safe:animate-pulse" />;
    case 'paragraph':
      return (
        <div data-skeleton class="space-y-1.5">
          {Array.from({ length: p.lines }).map((_, i) => (
            <div
              key={i}
              class={`h-3 ${i === 0 ? 'w-full' : 'w-3/4'} bg-outline-variant/30 rounded motion-safe:animate-pulse`}
            />
          ))}
        </div>
      );
    case 'form-fields':
      return (
        <div data-skeleton class="space-y-2">
          {Array.from({ length: p.count }).map((_, i) => (
            <div key={i} class="space-y-1">
              <div class="h-2 w-24 bg-outline-variant/40 rounded motion-safe:animate-pulse" />
              <div class="h-8 w-full bg-outline-variant/20 rounded motion-safe:animate-pulse" />
            </div>
          ))}
        </div>
      );
    case 'button':
      return <div data-skeleton class="h-10 w-32 bg-outline-variant/40 rounded motion-safe:animate-pulse" />;
    case 'method-tiles':
      return (
        <div data-skeleton class="grid grid-cols-2 gap-2">
          {Array.from({ length: p.count }).map((_, i) => (
            <div key={i} class="h-16 bg-outline-variant/30 rounded motion-safe:animate-pulse" />
          ))}
        </div>
      );
    case 'summary':
      return (
        <div data-skeleton class="space-y-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} class="h-3 w-full bg-outline-variant/30 rounded motion-safe:animate-pulse" />
          ))}
        </div>
      );
  }
}
